/**
 * r_ceas_workflow r_ceas_project を更新する関数（新規投資申請画面の確認ボタン押下時）
 *
 * @param {any} WebAPI - APIリクエストを実行するWebAPIコンテキスト
 * @param {string} projectId - プロジェクトID
 * @param {string} flowId - ワークフローID
 * @param {string} employeeId - 従業員ID
 * @param {string} divisionName - 部署名
 * @param {string} confirm - 確認フラグ
 * @returns {Promise<boolean | undefined>} 成功時は`true`、失敗時は`false`または`undefined`
 */
export const updateConfirm = async (
  WebAPI: any,
  projectId: string,
  flowId: string,
  employeeId: string,
  divisionName: string,
  confirm: string,
  reviewDate?: string | null,
): Promise<boolean | undefined> => {
  const functionName = 'updateConfirm';
  console.log(`${functionName} START`);
  const input = {
    projectId: projectId,
    flowId: flowId,
    employeeId: employeeId,
    divisionName: divisionName,
    confirm: confirm,
    ...(reviewDate && { reviewDate }),
  };
  console.log(`${functionName} INPUT: ${JSON.stringify(input)}`);
  const query = `
    mutation ${functionName} ($input: ConfirmInput!) {
      ${functionName}(input: $input) {status}
    }
  `;
  try {
    const result = await WebAPI.request({
      query: query,
      variables: { input }
    });
    console.log(`${functionName} RESULT: ${JSON.stringify(result)}`);
    return result?.data?.updateConfirm?.status === 'success';
  } catch (error) {
    console.error(`${functionName} ERROR: ${JSON.stringify(error)}`);
    return undefined;
  } finally {
    console.log(`${functionName} END`);
  }
};

/**
 * r_ceas_project から 確認担当者JSON を取得する関数（新規投資申請画面の初期表示時及び確認ボタン押下時）
 *
 * @param {any} WebAPI - APIリクエストを実行するWebAPIコンテキスト
 * @param {string} projectId - プロジェクトID
 * @returns {Promise<any | undefined>} 取得した確認担当者JSON、失敗時は`undefined`
 */
export const getConfirmor = async (
  WebAPI: any,
  projectId: string,
  reviewDate?: string | null,
): Promise<any | undefined> => {
  const functionName = 'getConfirmor';
  console.log(`${functionName} START`);
  console.log(`${functionName} projectId: ${projectId}`);
  const query = `
    query ${functionName}($projectId: String!, $reviewDate: String) {
      ${functionName}(projectId: $projectId, reviewDate: $reviewDate) {
        quantityAndPrice {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          salesDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        pricingAgreementForm {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          salesDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          procurementDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        scheduling {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          salesDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        productionProcess {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          procurementDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        equipment {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          procurementDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        profitAndLoss {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          companyHeadDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        projectProfitablityStatement {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          companyHeadDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        simulationNewProduct {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          companyHeadDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        simulationProductivityImprovement {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          companyHeadDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        reportAndDraft {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          companyHeadDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          salesDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          procurementDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        reviewSales {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          salesDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        reviewBusiness {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          companyHeadDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          procurementDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
        reviewFuture {
          companyDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          companyHeadDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          salesDivision{
            isConfirmor
            employeeName
            confirmTime
          }
          procurementDivision{
            isConfirmor
            employeeName
            confirmTime
          }
        }
      }
    }
  `;
  try {
    const result = await WebAPI.request({
      query: query,
      variables: { projectId, ...(reviewDate && { reviewDate }) },
    });
    console.log(`${functionName} RESULT: ${JSON.stringify(result)}`);
    return result?.data?.[functionName][0];
  } catch (error) {
    console.error(`${functionName} ERROR: ${JSON.stringify(error)}`);
    return undefined;
  } finally {
    console.log(`${functionName} END`);
  }
};
